'use client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { elowenClient, ElowenApiError } from './elowenClient';
import { useMe } from './queries';
import { useTranslation } from './i18n';
import { SEARCH_MAX_QUERY_CHARS } from './searchBounds';
import type { SearchAskCandidate, SearchAskResult } from './types';

export function useDocsCatalog() {
  const { locale } = useTranslation();
  const account = useMe().data?.user?.id;
  return useQuery({
    queryKey: ['docs', 'catalog', account, locale],
    queryFn: ({ signal }) => elowenClient.docsCatalog(locale, signal),
    enabled: account !== undefined,
    staleTime: 0,
    retry: false,
  });
}

export function useDocsArticle(slug: string) {
  const { locale } = useTranslation();
  const account = useMe().data?.user?.id;
  const catalog = useDocsCatalog();
  return useQuery({
    queryKey: ['docs', 'article', account, locale, catalog.data?.revision, slug],
    queryFn: ({ signal }) => elowenClient.docsArticle(slug, locale, signal),
    enabled: account !== undefined && catalog.isSuccess,
    retry: false,
  });
}

/** The page and palette share server retrieval, not another browser index. Never keep the previous
 * query's hits while its replacement is debouncing or loading. React Query owns request cancellation. */
export function useDocsSearch(query: string, group?: string) {
  const { locale } = useTranslation();
  const account = useMe().data?.user?.id;
  const catalog = useDocsCatalog();
  const trimmed = query.trim().slice(0, SEARCH_MAX_QUERY_CHARS);
  const [settled, setSettled] = useState('');
  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(trimmed), 300);
    return () => window.clearTimeout(timer);
  }, [trimmed]);
  const ready = settled === trimmed && trimmed.length > 0;
  const search = useQuery({
    queryKey: ['docs', 'search', account, locale, catalog.data?.revision, group ?? null, trimmed],
    queryFn: ({ signal }) => elowenClient.docsSearch(trimmed, locale, { limit: 8, ...(group ? { group } : {}) }, signal),
    enabled: ready && account !== undefined && catalog.isSuccess,
    retry: false,
    staleTime: 0,
  });
  // A search can answer from a corpus newer than the catalog this browser holds: a plugin with guides was
  // installed or a guide changed since the last catalog read. Every hit names an article of the response's
  // own revision, so those hits are held back and the catalog refetched until the two revisions agree.
  const stale = ready && search.data !== undefined && catalog.data !== undefined
    && search.data.revision !== catalog.data.revision;
  const refetchCatalog = catalog.refetch;
  useEffect(() => { if (stale) void refetchCatalog(); }, [stale, refetchCatalog]);
  return {
    ...search,
    catalog: catalog.data,
    data: ready && !search.isFetching && !catalog.isFetching && !stale ? search.data : undefined,
    isLoading: trimmed.length > 0 && (!ready || catalog.isPending || catalog.isFetching || search.isFetching || stale),
    isError: catalog.isError || search.isError,
    error: catalog.error ?? search.error,
    retry: () => catalog.isError ? catalog.refetch() : search.refetch(),
  };
}

type AskState =
  | { status: 'idle' | 'pending' }
  | { status: 'answered'; result: SearchAskResult }
  | { status: 'error'; error: unknown };
const IDLE: AskState = { status: 'idle' };
const NO_CANDIDATES: SearchAskCandidate[] = [];

/** One-shot inference per `run()`; no answer cache, conversation or retry. The palette runs it on an
 * explicit action, Guides once the query settles (`GuideSearch`). A new identity aborts the request in
 * flight, and the identity fence is also checked during render, so changing locale/query/account cannot
 * briefly paint the preceding answer. */
export function useDocsAsk(query: string, candidates: SearchAskCandidate[] = NO_CANDIDATES) {
  const { locale } = useTranslation();
  const account = useMe().data?.user?.id;
  const question = query.trim().slice(0, SEARCH_MAX_QUERY_CHARS);
  const identity = JSON.stringify([account, locale, question]);
  const active = useRef<AbortController | null>(null);
  const [state, setState] = useState<{ identity: string; value: AskState }>({ identity, value: IDLE });
  useEffect(() => {
    active.current?.abort();
    active.current = null;
    setState({ identity, value: IDLE });
    return () => active.current?.abort();
  }, [identity]);
  const run = useCallback(() => {
    active.current?.abort();
    const controller = new AbortController();
    active.current = controller;
    setState({ identity, value: { status: 'pending' } });
    void elowenClient.searchAsk(question, candidates, locale, controller.signal).then((result) => {
      if (!controller.signal.aborted) setState({ identity, value: { status: 'answered', result } });
    }).catch((error: unknown) => {
      if (!controller.signal.aborted) setState({ identity, value: { status: 'error', error } });
    });
  }, [identity, question, candidates, locale]);
  return { state: state.identity === identity ? state.value : IDLE, run };
}

export function docsAskError(error: unknown): 'aiUnavailable' | 'aiDenied' | 'aiLimited' | 'aiFailed' {
  if (error instanceof ElowenApiError) {
    if (error.status === 429) return 'aiLimited';
    if (error.status === 403) return 'aiDenied';
    if (error.status === 503) return 'aiUnavailable';
  }
  return 'aiFailed';
}
